import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Space, Input, message, Form, Tag, Modal, Tooltip, Typography, notification } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CopyOutlined } from '@ant-design/icons';
import CustomSelect from '../../components/CustomSelect';
import { CheckTableCustom } from '../../components/CheckTableCustom';
import NormalFormLabel from '../../components/NormalFormLabel';
import { LIST_APPEARANCE, LIST_POPUP_TYPE, LIST_STATUS_POPUP } from '../../constants/appSelect';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getListPopupRequest, deletePopupRequest, changePopupStatusRequest } from '../../services/popupRequest';
import { checkPermission } from '../../utils/checkPermission';
import { useSelector } from 'react-redux';
import { newObjectKeys } from '../../utils/utility';
import { getListAllPartnersRequest } from '../../services/memberRequest';
import { getListAllCampaignsRequest } from '../../services/campaignStatisticRequest';
import { CREATE_UPDATE_POPUP, DELETE_POPUP, GET_LIST_POPUPS, POPUP_STATUS_CHANGE } from '../../constants/apiEndPoint';
import { getListPopupPageRequest } from '../../services/commonRequest';
import { isEqual } from 'lodash';

const { Text } = Typography;

const QuanLyPopUp = () => {
  const [formSearch] = Form.useForm();
  const navigate = useNavigate();
  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;
  const [params, setParams] = useState({
    pageId: 1,
    limit: 20,
    status: [0, 1, 3, 4],
  });

  const {
    data: dataPopups,
    refetch: refetchDataPopup,
    isPending,
  } = useQuery({
    queryKey: ['dataPopups', params],
    queryFn: () => getListPopupRequest(params),
    enabled: checkPermission(GET_LIST_POPUPS, 'Xem danh sách'),
  });

  useEffect(() => {
    const saved = sessionStorage.getItem('filterStatePopups');
    if (saved) {
      const filters = JSON.parse(saved);
      formSearch.setFieldsValue(filters);
      setParams(filters);
    }
  }, []);

  const { data: dataAllPartners } = useQuery({
    queryKey: ['dataAllPartners'],
    queryFn: () => getListAllPartnersRequest(),
    enabled: !checkAccountIsPartner,
  });

  const { data: dataAllCampaigns } = useQuery({
    queryKey: ['dataAllCampaigns'],
    queryFn: () => getListAllCampaignsRequest(),
  });

  const dataPartnerOptions = useMemo(() => {
    const options =
      dataAllPartners?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataAllPartners]);

  const filteredCampaignOptions = useMemo(() => {
    const options =
      dataAllCampaigns?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataAllCampaigns]);

  // get data popups page
  const { data: dataAllPopupPage } = useQuery({
    queryKey: ['dataAllPopupPage'],
    queryFn: () => getListPopupPageRequest(),
  });

  // convert list popup page options
  const dataAllPopupPageOption = useMemo(() => {
    const options =
      dataAllPopupPage?.map((item) => {
        return { label: item?.name, value: item?.key };
      }) || [];
    return options;
  }, [dataAllPopupPage]);

  const { mutate: popupDelete } = useMutation({
    mutationFn: deletePopupRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa popup thành công!',
        });
        refetchDataPopup();
      } else {
        notification.error(data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!');
      }
    },
    onError: (error) => {
      notification.error({
        message: error?.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const { mutate: mutateChangePopupStatus } = useMutation({
    mutationFn: changePopupStatusRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Cập nhật popup thành công!',
        });
        refetchDataPopup();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleApprove = (data) => {
    // If receiving data from popup form (batch update)
    if (data.ids) {
      mutateChangePopupStatus(data);
    }
    // If receiving a single record from table action
    else if (data.id) {
      const newStatus = data.status === 0 ? 1 : 0;
      mutateChangePopupStatus({ id: data.id.toString(), status: newStatus });
    }
  };

  const handleDelete = (result) => {
    popupDelete(result);
  };

  const handleConfirm = (record, actionType) => {
    let modalConfig = {};
    if (actionType === 'delete') {
      if (!checkPermission(DELETE_POPUP, 'Xóa')) {
        message.error('Bạn không có quyền xóa banner này');
        return;
      }
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn có muốn xóa banner này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        okType: 'danger',
        onOk() {
          handleDelete(record.id);
        },
        onCancel() {},
      };
    } else if (actionType === 'approve') {
      if (!checkPermission(POPUP_STATUS_CHANGE, 'Duyệt')) {
        message.error('Bạn không có quyền duyệt banner này');
        return;
      }
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn có muốn duyệt banner này?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk() {
          handleApprove(record);
        },
        onCancel() {},
      };
    } else {
      return;
    }
    Modal.confirm(modalConfig);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Tiêu đề',
      dataIndex: ['title', 'id'],
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`/quan-ly-popup/${id}/chinh-sua-popup`} className="text-[#1677FF] hover:cursor-pointer">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Chiến dịch quảng cáo',
      dataIndex: ['campaignName', 'campaign_id'],
      key: 'campaign_id',
      render: (_, { campaignName, campaign_id }) => (
        <Link
          to={`/quan-ly-chien-dich/${campaign_id}/chinh-sua-chien-dich`}
          className="text-[#1677FF] hover:cursor-pointer"
        >
          {campaignName}
        </Link>
      ),
      minWidth: 150,
      width: 250,
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image) => <img src={image} alt="popup" style={{ width: '50px', height: '50px' }} />,
    },
    {
      title: 'Tập khách hàng',
      dataIndex: ['platform', 'version', 'topic', 'audience', 'type_topic'],
      key: ['audience', 'topic'],
      render: (_, { platform, version, topic, audience, type_topic }) => (
        <Space direction="vertical">
          {platform && <Text>{platform}</Text>}
          {version && Array.isArray(version) && version?.map((item) => <Text>{item}</Text>)}
          {topic && Array.isArray(topic) && topic?.map((item) => <Text>{item}</Text>)}
          {audience && Array.isArray(audience) && audience?.map((item) => <Text>{item}</Text>)}
        </Space>
      ),
    },
    {
      title: 'Vị trí hiển thị',
      dataIndex: 'page',
      key: 'page',
      width: 100,
    },
    {
      title: 'Loại popup',
      dataIndex: 'popup_type',
      key: 'popup_type',
      width: 100,
      render: (banner_type) => LIST_POPUP_TYPE?.find((item) => item?.value === banner_type)?.label,
    },
    {
      title: 'Thứ tự hiển thị',
      dataIndex: 'place',
      key: 'place',
      width: 90,
    },
    {
      title: 'Thứ tự con',
      dataIndex: 'sub_place',
      key: 'sub_place',
      width: 110,
    },
    {
      title: 'Cache client',
      dataIndex: 'not_cache_in_client',
      key: 'not_cache_in_client',
      width: 140,
      render: (not_cache_in_client) =>
        not_cache_in_client === 1 ? 'Không cache, luôn lấy mới' : 'Cho phép cache',
    },
    {
      title: 'Đối tác',
      dataIndex: 'partnerName',
      key: 'partnerName',
      minWidth: 100,
      width: 120,
    },
    {
      title: 'Số click tối đa',
      dataIndex: 'max_click',
      key: 'max_click',
      width: 120,
    },
    {
      title: 'Số click tối đa/ngày',
      dataIndex: 'max_click_per_date',
      key: 'max_click_per_date',
      width: 120,
    },
    {
      title: 'Số click tối đa/thiết bị',
      dataIndex: 'total_max_click',
      key: 'total_max_click',
      width: 120,
    },
    {
      title: 'Số impression tối đa',
      dataIndex: 'max_impression',
      key: 'max_impression',
      width: 120,
    },
    {
      title: 'Số impression tối đa/ngày',
      dataIndex: 'max_impression_per_date',
      key: 'max_impression_per_date',
      width: 120,
    },
    {
      title: 'Số impression tối đa/thiết bị',
      dataIndex: 'total_max_impression',
      key: 'total_max_impression',
      width: 120,
    },
    {
      title: 'Lý do ẩn',
      dataIndex: 'visible_reason',
      key: 'visible_reason',
      render: (_, { visible_reason }) => (
        <Space direction="vertical">
          {visible_reason && Array.isArray(visible_reason) && visible_reason?.map((item) => <Text>{item}</Text>)}
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'modify_time',
      key: 'modify_time',
      minWidth: 90,
      width: 120,
      render: (_, { modify_byName, modify_time }) => (
        <Space direction="vertical">
          <Text>{modify_byName}</Text>
          <Text>{modify_time}</Text>
        </Space>
      ),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'is_visible',
      key: 'is_visible',
      render: (is_visible) => (
        <Tag
          style={{
            backgroundColor: LIST_APPEARANCE.find((item) => item.value === is_visible)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_APPEARANCE.find((item) => item.value === is_visible)?.label}
        </Tag>
      ),
      fixed: 'right',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_POPUP.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_POPUP.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: 100,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(CREATE_UPDATE_POPUP, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa popup">
              <EditOutlined onClick={() => navigate(`/quan-ly-popup/${record?.id}/chinh-sua-popup`)} />
            </Tooltip>
          )}
          {checkPermission(DELETE_POPUP, 'Xóa') && record?.status !== 3 && (
            <Tooltip title="Xóa popup">
              <DeleteOutlined onClick={() => handleConfirm(record, 'delete')} />
            </Tooltip>
          )}
          {checkPermission(CREATE_UPDATE_POPUP, 'Sao chép') && (
            <Tooltip title="Sao chép popup">
              <CopyOutlined onClick={() => navigate(`/quan-ly-popup/${record?.id}/sao-chep-popup`)} />
            </Tooltip>
          )}
          {checkPermission(POPUP_STATUS_CHANGE, 'Duyệt') && record?.status === 0 && (
            <Tooltip title="Duyệt popup">
              <CheckOutlined onClick={() => handleConfirm(record, 'approve')} />
            </Tooltip>
          )}
        </Space>
      ),
      width: 120,
      fixed: 'right',
      hidden:
        !checkPermission(CREATE_UPDATE_POPUP, 'Chỉnh sửa') &&
        !checkPermission(DELETE_POPUP, 'Xóa') &&
        !checkPermission(CREATE_UPDATE_POPUP, 'Sao chép') &&
        !checkPermission(POPUP_STATUS_CHANGE, 'Duyệt'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };

    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }

    if (values?.page?.length > 0) {
      Object.assign(temp, { page: values?.page });
    }

    if (values?.campaign_id?.length) {
      Object.assign(temp, { campaign_id: values?.campaign_id });
    }

    if (values?.status?.length) {
      Object.assign(temp, { status: values?.status });
    } else {
      Object.assign(temp, { status: [0, 1, 3, 4] });
    }

    if (values?.partner_id?.length) {
      Object.assign(temp, { partner_id: values?.partner_id });
    }

    if (values?.visible >= 0) {
      Object.assign(temp, { visible: values?.visible });
    }

    if (isEqual(temp, params)) {
      refetchDataPopup();
    } else {
      sessionStorage.setItem('filterStatePopups', JSON.stringify(temp));
      setParams(temp);
    }
  };

  return (
    <div className="m-4">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách Popup</h1>
      </div>
      {checkPermission(GET_LIST_POPUPS, 'Xem danh sách') && (
        <>
          <Form
            name="search-popup"
            layout="vertical"
            form={formSearch}
            onFinish={handleSubmitForm}
            className="mb-5"
            requiredMark={false}
          >
            <>
              <div className="flex flex-wrap gap-2">
                <div className="flex-grow basis-[100%] sm:basis-[48%] lg:basis-[16%]">
                  <Form.Item label={<NormalFormLabel text="Tiêu đề Popup" />} name="title">
                    <Input placeholder="Tìm theo tiêu đề popup" />
                  </Form.Item>
                </div>

                <div className="flex-grow basis-[100%] sm:basis-[48%] lg:basis-[16%]">
                  <Form.Item label={<NormalFormLabel text="Chiến dịch quảng cáo" />} name="campaign_id">
                    <CustomSelect
                      placeholder="Chọn chiến dịch quảng cáo"
                      mode="multiple"
                      options={[...filteredCampaignOptions]}
                    />
                  </Form.Item>
                </div>

                {!checkAccountIsPartner && (
                  <div className="flex-grow basis-[100%] sm:basis-[48%] lg:basis-[16%]">
                    <Form.Item label={<NormalFormLabel text="Đối tác" />} name="partner_id">
                      <CustomSelect mode="multiple" placeholder="Chọn đối tác" options={[...dataPartnerOptions]} />
                    </Form.Item>
                  </div>
                )}

                <div className="flex-grow basis-[100%] sm:basis-[48%] lg:basis-[16%]">
                  <Form.Item label={<NormalFormLabel text="Vị trí hiển thị" />} name="page">
                    <CustomSelect placeholder="Chọn vị trí hiển thị" mode="multiple" options={dataAllPopupPageOption} />
                  </Form.Item>
                </div>
                <div className="flex-grow basis-[100%] sm:basis-[48%] lg:basis-[16%]">
                  <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status">
                    <CustomSelect placeholder="Trạng thái" mode="multiple" options={LIST_STATUS_POPUP} />
                  </Form.Item>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="flex-grow basis-[100%] sm:basis-[50%] lg:basis-[16%]">
                  <Form.Item label={<NormalFormLabel text="Hiển thị" />} name="visible" initialValue={-1}>
                    <CustomSelect
                      placeholder="Hiển thị"
                      options={[{ value: -1, label: 'Tất cả' }, ...LIST_APPEARANCE]}
                    />
                  </Form.Item>
                </div>
                <div className="flex-grow basis-[100%] sm:basis-[50%] lg:basis-[76%] pt-8">
                  <Form.Item>
                    <Space>
                      <Button type="primary" className=" " htmlType="submit">
                        <SearchOutlined /> Tìm kiếm
                      </Button>
                      {params &&
                        Object.values(
                          newObjectKeys(params, ['title', 'campaign_id', 'page', 'is_visible', 'status', 'partner_id'])
                        ).some((value) => (value && !Array.isArray(value)) || value?.length > 0) && (
                          <Button
                            className="w-[112px] h-[40px]"
                            htmlType="button"
                            onClick={() => {
                              formSearch.resetFields();

                              sessionStorage.setItem(
                                'filterStatePopups',
                                JSON.stringify({ pageId: 1, limit: params.limit, status: [0, 1, 3, 4] })
                              );

                              setParams({ pageId: 1, limit: params.limit, status: [0, 1, 3, 4] });
                            }}
                          >
                            <DeleteOutlined />
                            Xóa lọc
                          </Button>
                        )}
                    </Space>
                  </Form.Item>
                </div>
              </div>
            </>
          </Form>
          <CheckTableCustom
            isLoading={isPending}
            columns={columns}
            dataSource={dataPopups?.data}
            totalItem={dataPopups?.totalItem}
            params={params}
            setParams={(params) => {
              setParams(params);
              sessionStorage.setItem('filterStatePopups', JSON.stringify(params));
            }}
            handleApprove={handleApprove}
            roles="popups"
          />
        </>
      )}
    </div>
  );
};

export default QuanLyPopUp;
